import {Component, inject, input, output, ViewChild} from "@angular/core";
import {Translations} from "../../../../../../../sim.translations.barrel";
import {AutoLoginCapability, FontAwesomeCode, TrainingRoom} from "@models";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MatStep, MatStepLabel, MatStepper, MatStepperIcon} from "@angular/material/stepper";
import {MatButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {MatIcon} from "@angular/material/icon";
import {MatRipple} from "@angular/material/core";
import {MatTooltip} from "@angular/material/tooltip";
import {NgTemplateOutlet} from "@angular/common";
import {SharedModule} from "../../../../../../shared/shared.module";
import {TranslateModule} from "@ngx-translate/core";
import {NotificationService, SimDeviceService} from "@services";
import {SimButton} from "@common/ui/button/sim-button.component";
import {ButtonSegmentComponent} from "@common/ui/segmented-button/button-segment/button-segment.component";
import {SegmentedButtonComponent} from "@common/ui/segmented-button/segmented-button.component";

export interface SelectableTypes<T> {
	type: T;
	icon: FontAwesomeCode;
	translationKey: string;
}

@Component({
	selector: "sim-setup-auto-login",
	standalone: true,
	imports: [
		MatButton,
		MatDivider,
		MatIcon,
		MatRipple,
		MatStep,
		MatStepLabel,
		MatStepper,
		MatStepperIcon,
		MatTooltip,
		NgTemplateOutlet,
		SharedModule,
		TranslateModule,
		SimButton,
		ButtonSegmentComponent,
		SegmentedButtonComponent,
	],
	templateUrl: "./setup-auto-login.component.html",
	styleUrl: "./setup-auto-login.component.scss",
})
export class SetupAutoLoginComponent<T> {
	deviceId = input.required<string>();
	selectableDeviceTypes = input.required<SelectableTypes<T>[]>();
	getAutoLoginType = input.required<(room: TrainingRoom | null, type: T | null) => AutoLoginCapability>();

	deviceRegistered = output<void>();
	@ViewChild("stepper", {static: false}) stepper!: MatStepper;

	private _simDeviceService = inject(SimDeviceService);
	private _simNotificationService = inject(NotificationService);

	protected readonly T = Translations;
	protected isRoomSelectionPlanViewEnabled: boolean = false;

	protected setupAutoLoginForm: FormGroup<{
		roomSelection: FormControl<TrainingRoom | null>;
		deviceType: FormControl<T | null>;
	}> = new FormGroup({
		roomSelection: new FormControl<TrainingRoom | null>(null, [Validators.required]),
		deviceType: new FormControl<T | null>(null, [Validators.required]),
	});

	protected toggleRoomSelectionPlanView(checked: boolean) {
		this.isRoomSelectionPlanViewEnabled = checked;
	}

	selectRoom(selectedRoom: TrainingRoom) {
		this.setupAutoLoginForm.controls.roomSelection.setValue(selectedRoom);
		this.stepper.next();
	}

	setDeviceType(deviceType: {type: T; icon: FontAwesomeCode}) {
		this.setupAutoLoginForm.controls.deviceType.setValue(deviceType.type);
		this.stepper.next();
	}

	finishSetup() {
		if (this.setupAutoLoginForm.valid) {
			const autoLoginType: AutoLoginCapability = this.getAutoLoginType()(
				this.setupAutoLoginForm.controls.roomSelection.value,
				this.setupAutoLoginForm.controls.deviceType.value,
			);
			this._simDeviceService
				.setDeviceAutoLoginCapability(autoLoginType, this.deviceId())
				.then(() => {
					this.deviceRegistered.emit();
					this._simNotificationService.notifySuccess();
				})
				.catch((error) => {
					this._simNotificationService.notifyError(error.message);
				});
		}
	}
}
